import clamp from 'lodash/clamp';
import React, {useCallback, useEffect} from 'react';
import {requestContext} from '../../../../../../common/requestContext';

type SeekbarEvent =
  | globalThis.TouchEvent
  | globalThis.MouseEvent
  | React.TouchEvent
  | React.MouseEvent;

export function useSeekbarHandler({
  seekbarRef,
  onProgressChange,
  onReleaseSeekbar,
  onPressSeekbar,
}: {
  seekbarRef: React.RefObject<HTMLDivElement>;
  onProgressChange?: (newProgress: number) => void;
  onReleaseSeekbar?: ((newProgress: number) => void) | VoidFunction;
  onPressSeekbar?: VoidFunction;
}) {
  const isMobile = requestContext.requestInfo.display.isMobile;

  const getNewProgress = useCallback(
    ({e, seekbar}: {e: SeekbarEvent; seekbar: HTMLDivElement}) => {
      const {clientWidth} = seekbar;
      const boundingRect = seekbar.getBoundingClientRect();

      const clientX =
        'touches' in e
          ? e.touches.length > 0
            ? e.touches[0].clientX
            : e.changedTouches[0].clientX
          : e.clientX;

      const positionX = clientX - boundingRect.x;
      return clamp(positionX, 0, clientWidth) / clientWidth;
    },
    []
  );

  const handleProgressChange = useCallback(
    (e: SeekbarEvent) => {
      e.stopPropagation();

      const seekbar = seekbarRef.current;
      if (!seekbar) return;

      const newValue = getNewProgress({e, seekbar});
      onProgressChange && onProgressChange(newValue);
    },
    [seekbarRef.current, onProgressChange, getNewProgress]
  );

  const handleReleaseSeekbar = useCallback(
    (e: SeekbarEvent) => {
      e.stopPropagation();

      const moveEventName = 'touches' in e ? 'touchmove' : 'mousemove';
      window.removeEventListener(moveEventName, handleProgressChange);

      const seekbar = seekbarRef.current;
      if (!seekbar) return;

      if (isMobile) {
        onReleaseSeekbar && (onReleaseSeekbar as VoidFunction)();
      } else {
        const newValue = getNewProgress({e, seekbar});
        onReleaseSeekbar && onReleaseSeekbar(newValue);
      }
    },
    [
      seekbarRef.current,
      isMobile,
      handleProgressChange,
      onReleaseSeekbar,
      getNewProgress,
    ]
  );

  const handlePressSeekbar = useCallback(
    (e: SeekbarEvent) => {
      e.stopPropagation();

      const isTouch = 'touches' in e;
      // 모바일에서 마우스 이벤트를 핸들하지 않게 합니다
      if (isMobile && !isTouch) {
        return;
      }
      // 눌렸을때 prgress change를 한번 실행합니다
      handleProgressChange(e);

      const moveEventName = isTouch ? 'touchmove' : 'mousemove';
      // 프로그레스 바에 이벤트 핸들러를 할당하면 영역밖으로 나갔을 때 이벤트가 동작하지 않기 때문에
      // window 자체에 이벤트 핸들러를 할당해준다.
      window.addEventListener(moveEventName, handleProgressChange, {
        passive: false,
      });
      // mouseup 이벤트는 mousedown 이벤트가 일어난 곳 외에서 마우스를 떼면 발생하지 않아서 window에 걸어준다.
      if (!isTouch) {
        window.addEventListener('mouseup', handleReleaseSeekbar, {
          once: true,
        });
      }

      onPressSeekbar && onPressSeekbar();
    },
    [isMobile, handleProgressChange, handleReleaseSeekbar, onPressSeekbar]
  );

  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'hidden') {
        window.removeEventListener('touchmove', handleProgressChange);
        window.removeEventListener('mousemove', handleProgressChange);
      }
    };
    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [handleProgressChange]);

  return {
    handleProgressChange,
    handleReleaseSeekbar,
    handlePressSeekbar,
  };
}
